import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { TRANSLATION } from '../../constants'
import CompletedImg from '../../elements/SuccessBox/assets/completed.png'
import { Service } from '../../../types'
import InitialJobStategyImg from './assets/initial-job-strategy.png'
import InitialJobStategyMobileImg from './assets/search-strategy-mobile.png'

import ProgressJobStategyImg1 from './assets/progress-job-strategy-1.png'
import ProgressJobStategyImg2 from './assets/progress-job-strategy-2.png'
import ProgressJobStategyImg3 from './assets/progress-job-strategy-3.png'
import ProgressJobStategyImg4 from './assets/progress-job-strategy-4.png'

const actionLinksPerStep = [
  '/career-plans/execute-a-job-search?cp_top_level=3&cp_second_level=7',
  '/career-plans/execute-a-job-search?cp_top_level=3&cp_second_level=8',
  '/career-plans/execute-a-job-search?cp_top_level=3&cp_second_level=9',
  '/career-plans/execute-a-job-search?cp_top_level=3&cp_second_level=10',
]

const imagesPerStep = [
  ProgressJobStategyImg1,
  ProgressJobStategyImg2,
  ProgressJobStategyImg3,
  ProgressJobStategyImg4,
]

const initialTranslation = `${TRANSLATION}.job_strategy.initial`
const progressTranslation = `${TRANSLATION}.job_strategy.progress`
const completedTranslation = `${TRANSLATION}.job_strategy.completed`

/**
 * Generates the configuration for the Job Strategy widget based on a progress percentage.
 *
 * - Clamps the input progress between 0 and 100.
 * - If progress is 0, returns the “initial” state (initial text, image, alt text, and link).
 * - If progress is 100, returns the “completed” state (completed text, image, alt text, and link).
 * - Otherwise, divides progress into four 25% steps to pick the correct image and link.
 */
export function useJobStrategyConfig(props: { service: Service }) {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const { service } = props
  const progress = Math.max(0, Math.min(100, service.calculatedProgress.percentageProgress))

  const initialText = {
    badge: i18n.t(`${initialTranslation}.badge`),
    header: i18n.t(`${initialTranslation}.header`),
    subHeader: i18n.t(`${initialTranslation}.sub_header`),
    actionText: i18n.t(`${initialTranslation}.action_text`),
  }

  const progressText = {
    badge: i18n.t(`${progressTranslation}.badge`),
    header: i18n.t(`${progressTranslation}.header`),
    subHeader: i18n.t(`${progressTranslation}.sub_header`),
    actionText: i18n.t(`${progressTranslation}.action_text`),
  }

  const completedText = {
    badge: undefined,
    header: i18n.t(`${completedTranslation}.header`),
    subHeader: i18n.t(`${completedTranslation}.sub_header`),
    actionText: i18n.t(`${completedTranslation}.action_text`),
  }

  if (progress === 0) {
    return {
      text: initialText,
      status: 'initial',
      image: isPhone ? InitialJobStategyMobileImg : InitialJobStategyImg,
      imageAlt: i18n.t(`${initialTranslation}.image_alt`),
      actionLink: '/career-plans/execute-a-job-search?cp_top_level=3&cp_second_level=7',
    }
  }

  if (progress === 100) {
    return {
      text: completedText,
      status: 'completed',
      image: CompletedImg,
      imageAlt: i18n.t(`${completedTranslation}.image_alt`),
      actionLink: service.getNextActionUrl() ?? '#',
    }
  }

  const step = Math.ceil(progress / 25) - 1

  return {
    text: progressText,
    status: 'progress',
    image: imagesPerStep[step],
    imageAlt: i18n.t(`${progressTranslation}.image_alt`),
    actionLink: actionLinksPerStep[step],
  }
}
